// src/services/storage.js

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

// Upload a single file to Firebase Storage
export const uploadFile = async (path, file) => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error(`Error uploading file to path: ${path}`, error);
    throw error;
  }
};

// Generate a unique file path for uploads
export const generateFilePath = (jewellerId, type, fileName) => {
  const timestamp = Date.now();
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.]/g, '_');
  
  switch (type) {
    case 'profile':
      return `jewellers/${jewellerId}/profile/${timestamp}_${sanitizedFileName}`;
    case 'header':
      return `jewellers/${jewellerId}/header/${timestamp}_${sanitizedFileName}`;
    case 'gallery':
      return `jewellers/${jewellerId}/gallery/${timestamp}_${sanitizedFileName}`;
    case 'product':
      return `jewellers/${jewellerId}/products/${timestamp}_${sanitizedFileName}`;
    default:
      return `jewellers/${jewellerId}/uploads/${timestamp}_${sanitizedFileName}`;
  }
};

// Uploads an array of image files and returns an array of their URLs
export const uploadProductImages = async (jewellerId, productId, files) => {
    // If there are no files, return an empty array immediately.
    if (!files || files.length === 0) {
        return [];
    }

    // Create an array of upload promises.
    // Each promise resolves with the download URL of the uploaded file.
    try {
        const results = await Promise.allSettled(
            files.map(file => {
                const path = generateFilePath(jewellerId, 'product', file.name || 'upload');
                return uploadFile(path, file);
            })
        );

        const uploadedUrls = results
            .filter(result => result.status === 'fulfilled')
            .map(result => result.value);

        const failures = results
            .map((result, index) => {
                if (result.status === 'rejected') {
                    return { file: files[index], error: result.reason };
                }
                return null;
            })
            .filter(Boolean);

        if (failures.length > 0) {
            const failedNames = failures
                .map(failure => failure.file?.name || 'unknown file')
                .join(', ');
            const error = new Error(
                `Failed to upload ${failures.length} image${failures.length === 1 ? '' : 's'}: ${failedNames}`
            );
            error.name = 'ProductImageUploadError';
            error.failures = failures;
            error.uploadedUrls = uploadedUrls;
            throw error;
        }

        console.log("Successfully uploaded all images. URLs:", uploadedUrls);
        return uploadedUrls;
    } catch (error) {
        console.error("One or more image uploads failed:", error);
        throw error;
    }
};

// Delete a file from Firebase Storage
export const deleteFile = async (url) => {
  try {
    console.log('Attempting to delete file:', url);
    let fileRef;
    
    if (url.includes('firebase')) {
      const urlObj = new URL(url);
      const path = urlObj.pathname.split('/o/')[1];
      if (!path) {
        console.error('Invalid Firebase Storage URL format:', url);
        throw new Error('Invalid Firebase Storage URL format');
      }
      const decodedPath = decodeURIComponent(path.split('?')[0]);
      fileRef = ref(storage, decodedPath);
    } else {
      fileRef = ref(storage, url);
    }
    
    await deleteObject(fileRef);
    console.log('File deleted successfully');
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Generate thumbnail from video file at 1 second mark
 * @param {File} videoFile - The video file
 * @returns {Promise<File>} Thumbnail as File object
 */
export const generateVideoThumbnail = async (videoFile) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    
    // Configure video - IMPORTANT: need to actually load data, not just metadata
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    
    // Timeout after 30 seconds
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Thumbnail generation timeout'));
    }, 30000);
    
    const cleanup = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(video.src);
      video.remove();
      canvas.remove();
    };
    
    // When enough data loaded, seek to desired position
    video.addEventListener('loadeddata', () => {
      // Seek to 2 seconds or 20% of video duration, whichever is less
      const seekTime = Math.min(2, video.duration * 0.2);
      video.currentTime = seekTime;
    });
    
    // When seeked, wait a bit then capture the frame
    video.addEventListener('seeked', () => {
      // Small delay to ensure frame is rendered
      setTimeout(() => {
        // Calculate dimensions (max 1280x720)
        let width = video.videoWidth;
        let height = video.videoHeight;
        
        if (width > 1280) {
          height = (height * 1280) / width;
          width = 1280;
        }
        if (height > 720) {
          width = (width * 720) / height;
          height = 720;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw frame to canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], 'thumbnail.jpg', { 
                type: 'image/jpeg' 
              });
              cleanup();
              resolve(file);
            } else {
              cleanup();
              reject(new Error('Failed to create thumbnail'));
            }
          },
          'image/jpeg',
          0.85  // Quality
        );
      }, 100); // 100ms delay to ensure frame is rendered
    });
    
    video.addEventListener('error', () => {
      cleanup();
      reject(new Error('Video loading failed'));
    });
    
    // Load video
    video.src = URL.createObjectURL(videoFile);
    video.load();
  });
};

/**
 * Upload video with automatic thumbnail generation
 * @param {File} videoFile - The video file to upload
 * @param {string} jewellerId - The jeweller's ID
 * @returns {Promise<{videoUrl: string, thumbnailUrl: string}>}
 */
export const uploadVideoWithThumbnail = async (videoFile, jewellerId) => {
  // Validate file type
  if (!videoFile.type.startsWith('video/')) {
    throw new Error('Invalid file type. Please upload a video file.');
  }

  // Generate thumbnail from video
  const thumbnailBlob = await generateVideoThumbnail(videoFile);
  
  // Create unique filenames
  const timestamp = Date.now();
  const sanitizedName = videoFile.name.replace(/[^a-zA-Z0-9.]/g, '_');
  const videoFileName = `${timestamp}_${sanitizedName}`;
  const thumbnailFileName = `${timestamp}_thumb.jpg`;
  
  // Define storage paths
  const videoPath = `jewellers/${jewellerId}/videos/${videoFileName}`;
  const thumbnailPath = `jewellers/${jewellerId}/videos/thumbnails/${thumbnailFileName}`;
  
  // Upload both files in parallel (CORRECTED ORDER: path first, then file)
  const [videoUrl, thumbnailUrl] = await Promise.all([
    uploadFile(videoPath, videoFile),
    uploadFile(thumbnailPath, thumbnailBlob)
  ]);
  
  return { videoUrl, thumbnailUrl };
};